/**
 * Colas de atención: configuración y reglas puras (sin React ni I/O).
 *
 * Cada tipo de registro pertenece como máximo a una cola; la cola asigna el
 * responsable automáticamente al crear el ticket (trigger
 * `Tickets.assign_ticket_from_queue`, ver migración 20260918_attention_queues).
 */

import { getTicketType } from './ticketType'

/** Estrategias de asignación (CHECK de `Tickets.queue.strategy`). */
export const QUEUE_STRATEGIES = {
  least_loaded: {
    label: 'Menor carga',
    icon: 'fa-scale-balanced',
    hint: 'Asigna a quien tenga menos tickets abiertos.',
  },
  round_robin: {
    label: 'Rotación',
    icon: 'fa-arrows-rotate',
    hint: 'Reparte por turnos entre los miembros.',
  },
}

/** Colores sugeridos para identificar las colas. */
export const QUEUE_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#14b8a6',
  '#f97316',
  '#ef4444',
  '#22c55e',
  '#eab308',
  '#64748b',
]

/** Borrador inicial de una cola nueva. */
export const EMPTY_QUEUE = {
  name: '',
  description: '',
  color: QUEUE_COLORS[0],
  strategy: 'least_loaded',
  active: true,
  types: [],
  memberIds: [],
}

/**
 * Qué cola tiene hoy cada tipo, sin contar la cola que se está editando.
 * @param {Array<{id: number, name: string, types: string[]}>} queues
 * @param {number|null} [exceptQueueId] - Cola en edición (sus tipos quedan libres).
 * @returns {Record<string, {id: number, name: string}>} tipo → cola que lo tiene.
 */
export const typeOwners = (queues = [], exceptQueueId = null) => {
  const owners = {}
  for (const queue of queues) {
    if (queue.id === exceptQueueId) continue
    for (const type of queue.types || []) {
      owners[type] = { id: queue.id, name: queue.name }
    }
  }
  return owners
}

/**
 * Valida el borrador de una cola. Devuelve campo → mensaje (vacío si es válido).
 * @param {Object} draft - Ver `EMPTY_QUEUE`.
 * @param {Array<Object>} queues - Colas existentes (para nombres y tipos).
 * @param {number|null} [queueId] - Cola en edición.
 * @returns {Record<string, string>}
 */
export const validateQueue = (draft, queues = [], queueId = null) => {
  const errors = {}
  const name = draft.name?.trim().toLowerCase()

  if (!name) errors.name = 'Escriba un nombre para la cola.'
  else if (
    queues.some((q) => q.id !== queueId && q.name.trim().toLowerCase() === name)
  )
    errors.name = 'Ya existe una cola con ese nombre.'

  if (!QUEUE_STRATEGIES[draft.strategy])
    errors.strategy = 'Seleccione una estrategia.'

  if (!draft.types?.length)
    errors.types = 'Seleccione al menos un tipo de registro.'
  else {
    const owners = typeOwners(queues, queueId)
    const taken = draft.types.filter((t) => owners[t])
    if (taken.length)
      errors.types = `Ya pertenece(n) a otra cola: ${taken
        .map((t) => `${getTicketType(t).label} (${owners[t].name})`)
        .join(', ')}.`
  }

  if (draft.active && !draft.memberIds?.length)
    errors.memberIds = 'Una cola activa necesita al menos un miembro.'

  return errors
}

/** Color y nombre de la agrupación de tickets sin cola. */
export const NO_QUEUE_META = { id: null, name: 'Sin cola', color: '#94a3b8' }

/**
 * Resumen de tickets por cola para el dashboard: total, abiertos, completados,
 * vencidos, sin asignar y tiempo medio de resolución. Incluye "Sin cola" sólo
 * si hay tickets sin cola. Ordenado por total descendente.
 *
 * @param {Array<Object>} tickets - Tickets (queueId, completed, dueAt, responsibleId, createdAt, resolvedAt).
 * @param {Array<Object>} queues - Colas (id, name, color).
 * @param {number} [now=Date.now()]
 * @returns {Array<{id: number|null, name: string, color: string, total: number,
 *   open: number, completed: number, overdue: number, unassigned: number,
 *   avgResolutionMs: number|null}>}
 */
export const summarizeByQueue = (
  tickets = [],
  queues = [],
  now = Date.now()
) => {
  const byId = new Map(queues.map((q) => [q.id, q]))
  const groups = new Map()

  for (const t of tickets) {
    const meta = byId.get(t.queueId) || NO_QUEUE_META
    const key = meta.id ?? 'none'
    if (!groups.has(key)) {
      groups.set(key, {
        id: meta.id,
        name: meta.name,
        color: meta.color,
        total: 0,
        open: 0,
        completed: 0,
        overdue: 0,
        unassigned: 0,
        resolutionSum: 0,
        resolved: 0,
      })
    }
    const g = groups.get(key)
    g.total++
    if (t.completed) g.completed++
    else {
      g.open++
      if (t.dueAt && new Date(t.dueAt).getTime() < now) g.overdue++
    }
    if (!t.responsibleId) g.unassigned++
    if (t.resolvedAt && t.createdAt) {
      g.resolutionSum +=
        new Date(t.resolvedAt).getTime() - new Date(t.createdAt).getTime()
      g.resolved++
    }
  }

  return [...groups.values()]
    .map((g) => {
      const summary = {
        ...g,
        avgResolutionMs: g.resolved ? g.resolutionSum / g.resolved : null,
      }
      // Acumuladores internos: no forman parte del resultado.
      delete summary.resolutionSum
      delete summary.resolved
      return summary
    })
    .sort((a, b) => b.total - a.total)
}
